import { AsyncPipe } from '@angular/common';
import {
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    input,
    Input,
    OnInit,
    Output,
    ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import {
    getAddressComponent,
    getCityComponent,
    GoogleMapsAutocompleteDirective,
} from '@app/directives/google-maps-autocomplete/google-maps-autocomplete.directive';
import { LoadingCardComponent } from '@app/v2/components/loading-card/loading-card.component';
import {
    MdsAutocompleteComponent,
    MdsDropdownComponent,
    MdsOption,
    MdsRadioButton,
    MdsRadioGroupComponent,
    MdsTextFieldComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { CountryViewModel } from '@models/country';
import { CountryStateService } from '@services/state-management/country-state.service';
import { RegionStateService } from '@services/state-management/region-state.service';
import { SiteStateService } from '@services/state-management/site-state.service';
import {
    maxLengthValidator,
    notWhitespaceValidator,
    sameAddressValidator,
    zipCodeFormatValidator,
} from '@utility/utility.validators';
import { Observable, of } from 'rxjs';
import { filter, map, skipWhile, switchMap, tap } from 'rxjs/operators';
import { InputChange } from 'src/app/models/event-objects/input-change';
import { Address } from 'src/app/models/site';

@Component({
    selector: 'medpace-site-shipping-address',
    standalone: true,
    templateUrl: './site-shipping-address.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        AsyncPipe,
        MdsRadioGroupComponent,
        MdsTextFieldComponent,
        MdsDropdownComponent,
        MdsAutocompleteComponent,
        LoadingCardComponent,
        GoogleMapsAutocompleteDirective,
    ],
})
export class MedpaceSiteShippingAddressComponent implements OnInit {
    private stateService = inject(SiteStateService);
    private countryStateService = inject(CountryStateService);
    private regionStateService = inject(RegionStateService);
    private destroyRef = inject(DestroyRef);

    isModal = input<boolean>(false);
    addressFormGroup = new FormGroup({
        sline1Control: new FormControl<string>('', [
            Validators.required,
            notWhitespaceValidator(),
            maxLengthValidator(150),
        ]),
        sline2Control: new FormControl<string>('', [notWhitespaceValidator(), maxLengthValidator(100)]),
        scityControl: new FormControl<string>('', [
            Validators.required,
            notWhitespaceValidator(),
            maxLengthValidator(100),
        ]),
        sstateOrProvinceControl: new FormControl<string>('', [notWhitespaceValidator(), maxLengthValidator(25)]),
        spostalCodeControl: new FormControl<string>('', [notWhitespaceValidator(), zipCodeFormatValidator]),
        sregionControl: new FormControl<string>('', [
            Validators.required,
            notWhitespaceValidator(),
            maxLengthValidator(75),
        ]),
        scountryControl: new FormControl<string>('', [
            Validators.required,
            notWhitespaceValidator(),
            maxLengthValidator(65),
        ]),
    });
    sameAsPrimaryAddress: boolean = false;
    addressSameAsPrimaryControl = new FormControl<boolean>(this.sameAsPrimaryAddress);

    adressRadioButtons: MdsRadioButton[] = [
        {
            label: 'Yes',
            value: 'true',
            name: `yes`,
            id: `yes`,
        },
        {
            label: 'No',
            value: 'false',
            name: `no`,
            id: `no`,
        },
    ];

    @Input() isEditing: boolean;
    @Input() parentFormGroup: FormGroup;

    countries$: Observable<string[]> = this.countryStateService.getCountriesStringArray();
    emitCountryValue$: Observable<string>;

    @Output() inputChangeEvent = new EventEmitter<InputChange>();

    regions: any[] = [];
    filteredCountries: Observable<MdsOption[]>;
    loaderSpinner: boolean = true;
    regionStateType$ = of('Province / Region / State');

    regions$ = this.regionStateService.getRegions().pipe(
        map((regions) => {
            return regions.map((x) => ({ value: x.name, viewValue: x.name }));
        }),
        takeUntilDestroyed(this.destroyRef)
    );

    site$ = this.stateService.getSite().pipe(
        skipWhile((site) => site === null),
        tap((result) => {
            this.sameAsPrimaryAddress = result.isShippingAddressSameAsPrimary;
            this.parentFormGroup.controls.addressSameAsPrimary.setValue(this.sameAsPrimaryAddress ? 'true' : 'false');

            this.sameAsPrimaryAddress ? this.addressFormGroup.disable() : this.addressFormGroup.enable();
            if (!result.isShippingAddressSameAsPrimary && result?.shippingAddress) {
                this.setValues(result.shippingAddress);
            }

            this.loaderSpinner = false;
        }),
        takeUntilDestroyed(this.destroyRef)
    );

    ngOnInit(): void {
        this.initializeFormGroup();
        this.watchCountryChanges();
    }

    private watchCountryChanges(): void {
        this.addressFormGroup.controls.scountryControl.valueChanges
            .pipe(
                filter(Boolean),
                tap(
                    (countryName) => (this.regionStateType$ = this.countryStateService.getRegionStateType(countryName))
                ),
                switchMap((countryName) => this.countryStateService.getCountryByName(countryName)),
                filter(Boolean),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((country: CountryViewModel) => this.emitChange(country.viewValue, { id: 'shipping_country' }));
    }

    selectValue(event: MatRadioChange) {
        this.sameAsPrimaryAddress = event.value === 'true' ? true : false;
        !this.sameAsPrimaryAddress ? this.addressFormGroup.enable() : this.addressFormGroup.disable();
        this.inputChangeEvent.emit({ target: 'isShippingAddressSameAsPrimary', value: event.value });
    }

    googleMapsAutocompletePlaceChange(place: google.maps.places.Place) {
        const addressComponents = place.addressComponents ?? [];
        const line1 = `${getAddressComponent(
            'street_number',
            addressComponents
        )} ${getAddressComponent('route', addressComponents)}`;
        const city = getCityComponent(place);
        const state = getAddressComponent('administrative_area_level_1', addressComponents);
        const postalCode = getAddressComponent('postal_code', addressComponents);
        const country = getAddressComponent('country', addressComponents);

        this.addressFormGroup.controls.sline1Control.setValue(line1);
        this.addressFormGroup.controls.scityControl.setValue(city);
        this.addressFormGroup.controls.sstateOrProvinceControl.setValue(state);
        this.addressFormGroup.controls.spostalCodeControl.setValue(postalCode);
        this.addressFormGroup.controls.scountryControl.setValue(country);

        this.inputChangeEvent.emit({ target: 'shipping_line1', value: line1 });
        this.inputChangeEvent.emit({ target: 'shipping_city', value: city });
        this.inputChangeEvent.emit({ target: 'shipping_stateOrProvince', value: state });
        this.inputChangeEvent.emit({ target: 'shipping_postalCode', value: postalCode });
        this.inputChangeEvent.emit({ target: 'shipping_country', value: country });
    }

    initializeFormGroup() {
        this.parentFormGroup.addControl('addressFormGroup', this.addressFormGroup);
        this.parentFormGroup.addControl('addressSameAsPrimary', this.addressSameAsPrimaryControl);
        this.parentFormGroup.setValidators(sameAddressValidator('addressSameAsPrimary', 'addressFormGroup'));
    }

    setValues(address: Address) {
        this.addressFormGroup.controls.sline1Control.setValue(address.line1);
        this.addressFormGroup.controls.sline2Control.setValue(address.line2);
        this.addressFormGroup.controls.scityControl.setValue(address.city);
        this.addressFormGroup.controls.sstateOrProvinceControl.setValue(address.stateOrProvince);
        this.addressFormGroup.controls.spostalCodeControl.setValue(address.postalCode);
        this.addressFormGroup.controls.sregionControl.setValue(address.region);
        this.addressFormGroup.controls.scountryControl.setValue(address.country);
    }

    emitChange: (value: any, target: any) => void = (value: any, target: any) => {
        this.inputChangeEvent.emit({ target: target.id, value: value });
    };
    emitStringChange(event: Event, target: any) {
        const value = (event.target as HTMLInputElement).value;
        this.inputChangeEvent.emit({ target: target.id, value: value });
    }

    selected(event: any): void {
        this.inputChangeEvent.emit({ target: 'country', value: event.option.value });
    }
}
